'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import Menu from '../components/Menu';
import Footer from '../components/Footer';
import styles from './page.module.css';

export default function PaymentInfo() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const sectionRefs = {
    header: useRef(null),
    content: useRef(null)
  };

  useEffect(() => {
    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Animation for header and content
    gsap.from(sectionRefs.header.current, {
      y: 50,
      opacity: 0,
      duration: 1,
      delay: 0.5,
      ease: 'power3.out'
    });

    gsap.from(sectionRefs.content.current, {
      y: 50,
      opacity: 0,
      duration: 1,
      delay: 0.8,
      ease: 'power3.out'
    });
  }, [sectionRefs.header, sectionRefs.content]);

  return (
    <main>
      <Navbar toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
      <Menu isOpen={isMenuOpen} />
      
      <div className="container">
        <section className={styles.paymentInfo}>
          <div className={styles.header} ref={sectionRefs.header}>
            <div className={styles.metaIcon}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="24" fill="rgba(255, 255, 255, 0.1)" />
                <path d="M33 18H15C13.895 18 13 18.895 13 20V32C13 33.105 13.895 34 15 34H33C34.105 34 35 33.105 35 32V20C35 18.895 34.105 18 33 18Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M13 24H35" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17 29H23" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1>Payment Information</h1>
            <p className={styles.subheading}>Complete guide to payment methods and processes on GAM</p>
          </div>

          <div className={styles.content} ref={sectionRefs.content}>
            <div className={styles.infoCard}>
              <h2>Accepted Payment Methods</h2>
              <ul className={styles.paymentMethodsList}>
                <li>
                  <div className={styles.paymentMethod}>
                    <h3>Cryptocurrency</h3>
                    <p>We accept various cryptocurrencies including ETH, BNB, and USDT for purchasing GAM tokens.</p>
                    <div className={styles.supportedCryptos}>
                      <span>ETH</span>
                      <span>BNB</span>
                      <span>USDT</span>
                    </div>
                  </div>
                </li>
                <li>
                  <div className={styles.paymentMethod}>
                    <h3>Bank Transfers</h3>
                    <p>Direct bank transfers are available for larger purchases. Contact our support team for details.</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className={styles.infoCard}>
              <h2>Token Purchase Process</h2>
              <div className={styles.processSteps}>
                <div className={styles.step}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepContent}>
                    <h3>Connect Your Wallet</h3>
                    <p>Use our Web3 integration to connect your cryptocurrency wallet to our platform.</p>
                  </div>
                </div>
                <div className={styles.step}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepContent}>
                    <h3>Select Amount</h3>
                    <p>Choose how many GAM tokens you wish to purchase from our tiered offerings.</p>
                  </div>
                </div>
                <div className={styles.step}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepContent}>
                    <h3>Complete Payment</h3>
                    <p>Follow the prompts to complete your payment using your preferred method.</p>
                  </div>
                </div>
                <div className={styles.step}>
                  <div className={styles.stepNumber}>4</div>
                  <div className={styles.stepContent}>
                    <h3>Receive Tokens</h3>
                    <p>Your GAM tokens will be delivered directly to your connected wallet upon successful payment.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.infoCard}>
              <h2>Refund Policy</h2>
              <p>Due to the nature of blockchain transactions, all purchases of GAM tokens are final and non-refundable. Please ensure you are comfortable with your purchase before completing the transaction.</p>
            </div>

            <div className={styles.infoCard}>
              <h2>Transaction Security</h2>
              <p>All payments on our platform are processed through secure, encrypted channels. For cryptocurrency transactions, we leverage the security of blockchain technology. For credit card payments, we use industry-standard security protocols to protect your information.</p>
            </div>

            <div className={styles.infoCard}>
              <h2>Customer Support</h2>
              <p>If you encounter any issues during the payment process or have questions about your purchase, our support team is available to assist you. Please contact us at <a href="mailto:support@gam.one" className={styles.link}>support@gam.one</a>.</p>
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </main>
  );
} 
